export type DifficultyLevel = 'Easy' | 'Moderate' | 'Challenging' | 'Extreme';
export type TripStatus = 'Published' | 'Draft' | 'Active' | 'Completed' | 'Archived';
export type BookingStatus = 'Pending' | 'Confirmed' | 'Paid' | 'Cancelled';
export type PaymentStatus = 'Unpaid' | 'Partial' | 'Paid' | 'Refunded';

export interface Trip {
  id: string;
  title: string;
  location: string;
  state: string;
  description: string;
  shortDesc: string;
  price: number;
  duration: number;
  difficulty: DifficultyLevel;
  status: TripStatus;
  startDate: string;
  endDate: string;
  totalSeats: number;
  bookedSeats: number;
  images: string[];
  highlights: string[];
  inclusions: string[];
  exclusions: string[];
  itinerary: { day: number; title: string; description: string }[];
  meetingPoint: string;
  cancellationPolicy: string;
  minAge: number;
  maxGroupSize: number;
  category: string;
  featured: boolean;
  createdAt: string;
}

export interface Booking {
  id: string;
  tripId: string;
  tripTitle: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  seats: number;
  travelDate: string;
  totalAmount: number;
  paymentStatus: PaymentStatus;
  bookingStatus: BookingStatus;
  paymentId: string;
  createdAt: string;
  notes: string;
}

export const MOCK_TRIPS: Trip[] = [
  {
    id: 'trip-001',
    title: 'Kedarkantha Winter Trek',
    location: 'Uttarkashi',
    state: 'Uttarakhand',
    description: 'Experience the magic of a winter wonderland on the Kedarkantha trek. Covered in a thick blanket of snow from December to April, this trek offers stunning views of Swargarohini, Bandarpoonch, and Kalanag peaks. The summit at 12,500 ft rewards you with a 360° panorama of the Himalayan giants.',
    shortDesc: 'Snow-covered summit trek with panoramic Himalayan views at 12,500 ft.',
    price: 8500,
    duration: 6,
    difficulty: 'Moderate',
    status: 'Published',
    startDate: '2026-05-10',
    endDate: '2026-05-15',
    totalSeats: 20,
    bookedSeats: 17,
    images: [
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&q=80',
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
      'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80',
      'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800&q=80',
    ],
    highlights: ['Summit at 12,500 ft', 'Snow camping experience', '360° Himalayan panorama', 'Oak and pine forest trails'],
    inclusions: ['Accommodation (camps/guesthouses)', 'All meals (veg)', 'Experienced trek leader', 'First aid kit', 'Forest permits'],
    exclusions: ['Travel to Dehradun', 'Personal gear', 'Travel insurance', 'Porter charges'],
    itinerary: [
      { day: 1, title: 'Dehradun to Sankri', description: 'Drive from Dehradun to Sankri base camp. Evening briefing and gear check.' },
      { day: 2, title: 'Sankri to Juda Ka Talab', description: 'Trek through dense oak forests to the frozen lake at 9,100 ft.' },
      { day: 3, title: 'Juda Ka Talab to Kedarkantha Base', description: 'Ascend through snow fields to base camp at 11,250 ft.' },
      { day: 4, title: 'Summit Day', description: 'Pre-dawn start for the summit push. Reach 12,500 ft by 8 AM for sunrise.' },
      { day: 5, title: 'Base to Sankri', description: 'Descend via alternate route through snow-laden meadows.' },
      { day: 6, title: 'Sankri to Dehradun', description: 'Return drive to Dehradun with certificates and memories.' },
    ],
    meetingPoint: 'Dehradun ISBT Bus Stand, Gate 3',
    cancellationPolicy: 'Full refund up to 15 days before departure. 50% refund 7–14 days before. No refund within 7 days.',
    minAge: 14,
    maxGroupSize: 20,
    category: 'Trek',
    featured: true,
    createdAt: '2026-01-15',
  },
  {
    id: 'trip-002',
    title: 'Rishikesh River Rafting Expedition',
    location: 'Rishikesh',
    state: 'Uttarakhand',
    description: 'Tackle the mighty Ganges on a white-water rafting adventure through Grade III–IV rapids. The 26 km stretch from Shivpuri to Rishikesh is India\'s most iconic rafting route, passing through rapids like Golf Course, Club House, and the legendary Three Blind Mice.',
    shortDesc: 'Grade III–IV white-water rafting on 26 km of the mighty Ganges.',
    price: 3200,
    duration: 2,
    difficulty: 'Moderate',
    status: 'Published',
    startDate: '2026-05-03',
    endDate: '2026-05-04',
    totalSeats: 16,
    bookedSeats: 9,
    images: [
      'https://images.unsplash.com/photo-1530866495561-507c9faab2ed?w=800&q=80',
      'https://images.unsplash.com/photo-1504701954957-2010ec3bcec1?w=800&q=80',
      'https://images.unsplash.com/photo-1601643157091-ce5c665179ab?w=800&q=80',
      'https://images.unsplash.com/photo-1533130061792-64b345e4a833?w=800&q=80',
    ],
    highlights: ['26 km Shivpuri to Rishikesh route', 'Grade III–IV rapids', 'Cliff jumping optional', 'Bonfire and camping'],
    inclusions: ['Rafting equipment', 'Safety gear', 'Certified guide', 'Campsite stay', 'Breakfast and dinner'],
    exclusions: ['Travel to Rishikesh', 'Lunch', 'Personal expenses', 'Photography charges'],
    itinerary: [
      { day: 1, title: 'Arrival and Camp Setup', description: 'Check-in at riverside camp. Evening bonfire and safety briefing.' },
      { day: 2, title: 'Full Rafting Day', description: 'Start at Shivpuri, tackle all major rapids, end at Ram Jhula by afternoon.' },
    ],
    meetingPoint: 'Rishikesh Railway Station, Main Exit',
    cancellationPolicy: 'Full refund up to 7 days before. 50% refund 3–7 days before. No refund within 3 days.',
    minAge: 16,
    maxGroupSize: 16,
    category: 'Water Sports',
    featured: true,
    createdAt: '2026-01-20',
  },
  {
    id: 'trip-003',
    title: 'Hampta Pass High Altitude Trek',
    location: 'Manali',
    state: 'Himachal Pradesh',
    description: 'Cross from the lush Kullu Valley to the barren moonscape of Lahaul-Spiti through the Hampta Pass at 14,100 ft. This dramatic landscape change in a single day is what makes Hampta Pass one of India\'s most photogenic treks. Optional Chandratal lake extension available.',
    shortDesc: 'Dramatic valley crossover trek from Kullu to Spiti at 14,100 ft.',
    price: 12000,
    duration: 5,
    difficulty: 'Challenging',
    status: 'Published',
    startDate: '2026-06-15',
    endDate: '2026-06-19',
    totalSeats: 15,
    bookedSeats: 6,
    images: [
      'https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?w=800&q=80',
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80',
      'https://images.unsplash.com/photo-1454496522488-7a8e488e8606?w=800&q=80',
      'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80',
    ],
    highlights: ['Pass at 14,100 ft', 'Dramatic valley contrast', 'Optional Chandratal lake', 'Riverside camping'],
    inclusions: ['All accommodation', 'All meals', 'Trek leader + support staff', 'Permits', 'Medical kit'],
    exclusions: ['Travel to Manali', 'Personal insurance', 'Mule/porter charges', 'Alcoholic beverages'],
    itinerary: [
      { day: 1, title: 'Manali to Jobra', description: 'Drive to Jobra (10,400 ft) and begin trek to Chika campsite.' },
      { day: 2, title: 'Chika to Balu Ka Ghera', description: 'Trek through alpine meadows to the base of Hampta Pass.' },
      { day: 3, title: 'Pass Crossing Day', description: 'Cross Hampta Pass at 14,100 ft and descend to Shea Goru in Lahaul.' },
      { day: 4, title: 'Shea Goru to Chatru', description: 'Trek down the Lahaul side to Chatru on the Manali-Kaza highway.' },
      { day: 5, title: 'Return to Manali', description: 'Drive back to Manali via Rohtang Pass. Trip ends by 2 PM.' },
    ],
    meetingPoint: 'Manali Bus Stand, Old Manali Road',
    cancellationPolicy: 'Full refund up to 20 days before. 50% refund 10–20 days before. No refund within 10 days.',
    minAge: 16,
    maxGroupSize: 15,
    category: 'Trek',
    featured: true,
    createdAt: '2026-02-01',
  },
  {
    id: 'trip-004',
    title: 'Coorg Coffee Plantation Camping',
    location: 'Madikeri',
    state: 'Karnataka',
    description: 'Escape to the misty hills of Coorg for a weekend of camping amidst lush coffee and spice plantations. Trek through aromatic estates, spot wildlife at Nagarhole buffer zone, and experience a traditional Kodava homestay evening with local cuisine.',
    shortDesc: 'Weekend camping and trekking through Coorg\'s famous coffee estates.',
    price: 5500,
    duration: 3,
    difficulty: 'Easy',
    status: 'Published',
    startDate: '2026-05-16',
    endDate: '2026-05-18',
    totalSeats: 18,
    bookedSeats: 11,
    images: [
      'https://images.unsplash.com/photo-1470770903676-69b98201ea1c?w=800&q=80',
      'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80',
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80',
      'https://images.unsplash.com/photo-1542401886-65d6c61db217?w=800&q=80',
    ],
    highlights: ['Coffee estate walk', 'Nagarhole wildlife buffer', 'Traditional Kodava dinner', 'Stargazing night'],
    inclusions: ['Camping gear', 'All meals', 'Plantation guide', 'Kodava homestay evening', 'Transport within Coorg'],
    exclusions: ['Travel to Madikeri', 'Alcoholic beverages', 'Personal expenses'],
    itinerary: [
      { day: 1, title: 'Arrival and Plantation Walk', description: 'Arrive by noon. Coffee estate tour and introduction to spice farming.' },
      { day: 2, title: 'Forest Trek and Wildlife Watch', description: 'Morning trek along Nagarhole buffer zone. Afternoon birdwatching.' },
      { day: 3, title: 'Kodava Experience and Departure', description: 'Morning yoga, traditional breakfast, and departure by 11 AM.' },
    ],
    meetingPoint: 'Madikeri Bus Stand, KFC Corner',
    cancellationPolicy: 'Full refund up to 7 days before. No refund within 7 days.',
    minAge: 10,
    maxGroupSize: 18,
    category: 'Camping',
    featured: true,
    createdAt: '2026-02-10',
  },
  {
    id: 'trip-005',
    title: 'Spiti Valley Motorcycle Expedition',
    location: 'Kaza',
    state: 'Himachal Pradesh',
    description: 'Ride through one of the most remote valleys on Earth. The Spiti Valley motorcycle expedition covers 1,200 km of high-altitude desert roads, crossing passes like Rohtang (13,050 ft), Kunzum (15,059 ft), and Baralacha La (16,040 ft). A bucket-list ride for serious motorcyclists.',
    shortDesc: '1,200 km high-altitude motorcycle expedition through Spiti Valley.',
    price: 28000,
    duration: 9,
    difficulty: 'Extreme',
    status: 'Published',
    startDate: '2026-07-05',
    endDate: '2026-07-13',
    totalSeats: 10,
    bookedSeats: 10,
    images: [
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
      'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&q=80',
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
      'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800&q=80',
    ],
    highlights: ['Kunzum Pass at 15,059 ft', 'Key Monastery visit', 'Chandratal lake', '1,200 km total distance'],
    inclusions: ['Royal Enfield Himalayan rental', 'Fuel costs', 'All accommodation', 'Breakfast and dinner', 'Mechanic support vehicle'],
    exclusions: ['Travel to Manali', 'Riding gear (rental available)', 'Lunch', 'Personal expenses'],
    itinerary: [
      { day: 1, title: 'Manali — Bike pickup and briefing', description: 'Collect bikes, mandatory safety briefing, short city ride.' },
      { day: 2, title: 'Manali to Gramphoo', description: 'Cross Rohtang Pass (13,050 ft) into Lahaul valley.' },
      { day: 3, title: 'Gramphoo to Kaza via Kunzum', description: 'Epic day crossing Kunzum Pass (15,059 ft) into Spiti.' },
      { day: 4, title: 'Kaza — Monastery circuit', description: 'Visit Key, Kibber, and Langza monasteries.' },
      { day: 5, title: 'Kaza to Chandratal', description: 'Ride to the crescent moon lake at 14,100 ft. Camp overnight.' },
      { day: 6, title: 'Chandratal to Sarchu', description: 'Cross into Leh district via Baralacha La (16,040 ft).' },
      { day: 7, title: 'Sarchu to Leh', description: 'Descend to Leh via Tanglang La (17,582 ft) — highest motorable point.' },
      { day: 8, title: 'Leh — Rest and exploration', description: 'Free day in Leh. Optional Khardung La day trip.' },
      { day: 9, title: 'Fly out from Leh', description: 'Drop at Kushok Bakula Airport. Trip concludes.' },
    ],
    meetingPoint: 'Manali Old Bus Stand, Himalayan Adventures Office',
    cancellationPolicy: 'Full refund up to 30 days before. 50% refund 15–30 days before. No refund within 15 days.',
    minAge: 21,
    maxGroupSize: 10,
    category: 'Motorcycle',
    featured: false,
    createdAt: '2026-02-15',
  },
  {
    id: 'trip-006',
    title: 'Valley of Flowers National Park',
    location: 'Chamoli',
    state: 'Uttarakhand',
    description: 'Trek to the UNESCO World Heritage Valley of Flowers, a breathtaking high-altitude Himalayan meadow carpeted with hundreds of species of wildflowers. Best visited July–August when the valley is in full bloom. Combined with a visit to Hemkund Sahib Gurudwara at 14,200 ft.',
    shortDesc: 'UNESCO World Heritage trek through a Himalayan wildflower meadow.',
    price: 9800,
    duration: 6,
    difficulty: 'Moderate',
    status: 'Published',
    startDate: '2026-07-20',
    endDate: '2026-07-25',
    totalSeats: 22,
    bookedSeats: 14,
    images: [
      'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80',
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&q=80',
      'https://images.unsplash.com/photo-1542401886-65d6c61db217?w=800&q=80',
      'https://images.unsplash.com/photo-1470770903676-69b98201ea1c?w=800&q=80',
    ],
    highlights: ['UNESCO World Heritage site', '500+ wildflower species', 'Hemkund Sahib visit', 'Bhyundar Valley camps'],
    inclusions: ['All accommodation', 'All vegetarian meals', 'Trek permit', 'Guide and support staff', 'First aid'],
    exclusions: ['Travel to Haridwar/Rishikesh', 'Pony/porter charges', 'Personal insurance', 'Non-veg meals'],
    itinerary: [
      { day: 1, title: 'Haridwar to Govindghat', description: 'Drive from Haridwar to Govindghat base (6,100 ft).' },
      { day: 2, title: 'Govindghat to Ghangaria', description: 'Trek 14 km to Ghangaria (10,200 ft) — gateway to Valley of Flowers.' },
      { day: 3, title: 'Valley of Flowers Exploration', description: 'Full day in the valley. Photography and flora identification.' },
      { day: 4, title: 'Hemkund Sahib', description: 'Trek to Hemkund Sahib Gurudwara at 14,200 ft. Sacred alpine lake.' },
      { day: 5, title: 'Ghangaria to Govindghat', description: 'Descend back to Govindghat. Evening at Joshimath.' },
      { day: 6, title: 'Return to Haridwar', description: 'Drive back to Haridwar. Trip concludes by evening.' },
    ],
    meetingPoint: 'Haridwar Railway Station, Platform 1 Exit',
    cancellationPolicy: 'Full refund up to 15 days before. 50% refund 7–15 days before. No refund within 7 days.',
    minAge: 12,
    maxGroupSize: 22,
    category: 'Trek',
    featured: true,
    createdAt: '2026-02-20',
  },
  {
    id: 'trip-007',
    title: 'Andaman Scuba Diving Package',
    location: 'Havelock Island',
    state: 'Andaman & Nicobar',
    description: 'Discover the vibrant coral reefs of the Andaman Sea with a 4-day scuba diving package at Havelock Island. Dive at Nemo Reef, The Wall, and Barracuda City. Suitable for beginners (PADI Open Water course included) and certified divers.',
    shortDesc: '4-day scuba diving package with PADI certification option at Havelock.',
    price: 22000,
    duration: 4,
    difficulty: 'Easy',
    status: 'Published',
    startDate: '2026-05-25',
    endDate: '2026-05-28',
    totalSeats: 12,
    bookedSeats: 5,
    images: [
      'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80',
      'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800&q=80',
      'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?w=800&q=80',
      'https://images.unsplash.com/photo-1530866495561-507c9faab2ed?w=800&q=80',
    ],
    highlights: ['3 dive sites', 'PADI Open Water option', 'Radhanagar Beach sunset', 'Sea turtle encounters'],
    inclusions: ['All dives with equipment', 'PADI certification fee', 'Resort accommodation', 'All meals', 'Ferry transfers'],
    exclusions: ['Flight to Port Blair', 'Alcoholic beverages', 'Personal expenses', 'Tips'],
    itinerary: [
      { day: 1, title: 'Arrival and Pool Session', description: 'Ferry to Havelock. Afternoon pool training for beginners.' },
      { day: 2, title: 'First Ocean Dive', description: 'Morning open water dive at Nemo Reef. Afternoon theory.' },
      { day: 3, title: 'Advanced Dive Sites', description: 'The Wall and Barracuda City dives. Afternoon snorkeling.' },
      { day: 4, title: 'Final Dive and Departure', description: 'Morning dive, certification completion, ferry to Port Blair.' },
    ],
    meetingPoint: 'Port Blair Ferry Terminal, Gate 2',
    cancellationPolicy: 'Full refund up to 21 days before. 50% refund 10–21 days before. No refund within 10 days.',
    minAge: 15,
    maxGroupSize: 12,
    category: 'Water Sports',
    featured: false,
    createdAt: '2026-03-01',
  },
  {
    id: 'trip-008',
    title: 'Rajasthan Desert Safari',
    location: 'Jaisalmer',
    state: 'Rajasthan',
    description: 'Experience the golden dunes of the Thar Desert on a 3-night camel and jeep safari from Jaisalmer. Camp under a sky full of stars at Sam Sand Dunes, witness the desert sunrise from atop a dune, and visit ancient desert villages.',
    shortDesc: '3-night camel and jeep safari through Jaisalmer\'s golden Thar Desert.',
    price: 7200,
    duration: 4,
    difficulty: 'Easy',
    status: 'Published',
    startDate: '2026-05-08',
    endDate: '2026-05-11',
    totalSeats: 20,
    bookedSeats: 8,
    images: [
      'https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=800&q=80',
      'https://images.unsplash.com/photo-1533130061792-64b345e4a833?w=800&q=80',
      'https://images.unsplash.com/photo-1542401886-65d6c61db217?w=800&q=80',
      'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80',
    ],
    highlights: ['Sam Sand Dunes sunset', 'Camel safari', 'Desert village visit', 'Rajasthani folk performance'],
    inclusions: ['Desert camp accommodation', 'All meals', 'Camel ride', 'Jeep safari', 'Cultural evening'],
    exclusions: ['Travel to Jaisalmer', 'Alcoholic beverages', 'Tips', 'Personal shopping'],
    itinerary: [
      { day: 1, title: 'Jaisalmer Fort and City Tour', description: 'Afternoon city tour. Evening drive to Sam Sand Dunes.' },
      { day: 2, title: 'Camel Safari Day', description: 'Full day camel safari through remote dunes and villages.' },
      { day: 3, title: 'Jeep Safari and Kuldhara', description: 'Jeep safari to abandoned village Kuldhara. Desert photography.' },
      { day: 4, title: 'Sunrise and Departure', description: 'Sunrise from the dunes. Return to Jaisalmer by 9 AM.' },
    ],
    meetingPoint: 'Jaisalmer Railway Station, Main Exit',
    cancellationPolicy: 'Full refund up to 10 days before. No refund within 10 days.',
    minAge: 8,
    maxGroupSize: 20,
    category: 'Safari',
    featured: false,
    createdAt: '2026-03-05',
  },
  {
    id: 'trip-009',
    title: 'Meghalaya Living Root Bridges Trek',
    location: 'Cherrapunji',
    state: 'Meghalaya',
    description: 'Explore the mystical living root bridges of Meghalaya — centuries-old bridges formed by training Ficus roots across streams. Trek through dense subtropical rainforests, swim in crystal-clear pools, and visit the double-decker root bridge at Nongriat village.',
    shortDesc: 'Trek to centuries-old living root bridges in Meghalaya\'s rainforests.',
    price: 11500,
    duration: 5,
    difficulty: 'Challenging',
    status: 'Published',
    startDate: '2026-06-01',
    endDate: '2026-06-05',
    totalSeats: 14,
    bookedSeats: 12,
    images: [
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80',
      'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80',
      'https://images.unsplash.com/photo-1542401886-65d6c61db217?w=800&q=80',
      'https://images.unsplash.com/photo-1470770903676-69b98201ea1c?w=800&q=80',
    ],
    highlights: ['Double-decker root bridge', 'Rainbow Falls swim', 'Mawsmai cave', 'Local Khasi homestay'],
    inclusions: ['Homestay accommodation', 'All meals (local cuisine)', 'Experienced guide', 'Permits', 'First aid'],
    exclusions: ['Flight to Guwahati/Shillong', 'Personal gear', 'Travel insurance', 'Alcoholic beverages'],
    itinerary: [
      { day: 1, title: 'Shillong to Cherrapunji', description: 'Drive to Cherrapunji. Visit Mawsmai cave and Seven Sisters Falls.' },
      { day: 2, title: 'Nongriat Trek Day 1', description: 'Descend 3,500 steps to Nongriat village. Single root bridge visit.' },
      { day: 3, title: 'Double Decker Bridge', description: 'Trek to the famous double-decker root bridge. Swim at Rainbow Falls.' },
      { day: 4, title: 'Return Trek', description: 'Ascend back to Tyrna village. Afternoon rest at Cherrapunji.' },
      { day: 5, title: 'Dawki and Departure', description: 'Morning visit to crystal Umngot river at Dawki. Return to Shillong.' },
    ],
    meetingPoint: 'Shillong Police Bazaar, MTDC Office',
    cancellationPolicy: 'Full refund up to 15 days before. 50% refund 7–15 days before. No refund within 7 days.',
    minAge: 14,
    maxGroupSize: 14,
    category: 'Trek',
    featured: false,
    createdAt: '2026-03-10',
  },
  {
    id: 'trip-010',
    title: 'Chopta Tungnath Chandrashila',
    location: 'Rudraprayag',
    state: 'Uttarakhand',
    description: 'Trek to Tungnath — the highest Shiva temple in the world at 12,073 ft — and the Chandrashila summit at 13,123 ft. This short but spectacular trek through the Bugyals (high-altitude meadows) of Chopta offers sweeping views of Nanda Devi, Trishul, and Chaukhamba.',
    shortDesc: 'Trek to the world\'s highest Shiva temple with Himalayan panoramas.',
    price: 6500,
    duration: 3,
    difficulty: 'Moderate',
    status: 'Published',
    startDate: '2026-05-22',
    endDate: '2026-05-24',
    totalSeats: 16,
    bookedSeats: 13,
    images: [
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&q=80',
      'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800&q=80',
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
      'https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?w=800&q=80',
    ],
    highlights: ['Tungnath temple at 12,073 ft', 'Chandrashila summit 13,123 ft', 'Rhododendron forests', '360° Himalayan views'],
    inclusions: ['Accommodation (guesthouses)', 'All meals', 'Trek guide', 'Temple entry', 'First aid'],
    exclusions: ['Travel to Rishikesh/Haridwar', 'Personal gear', 'Porter charges'],
    itinerary: [
      { day: 1, title: 'Haridwar to Chopta', description: 'Drive from Haridwar to Chopta (8,790 ft) through Devprayag.' },
      { day: 2, title: 'Chopta to Chandrashila', description: 'Trek via Tungnath temple to Chandrashila summit. Back to Chopta.' },
      { day: 3, title: 'Return to Haridwar', description: 'Early morning drive back. Trip ends at Haridwar by evening.' },
    ],
    meetingPoint: 'Haridwar Railway Station, Platform 1 Exit',
    cancellationPolicy: 'Full refund up to 7 days before. No refund within 7 days.',
    minAge: 12,
    maxGroupSize: 16,
    category: 'Trek',
    featured: false,
    createdAt: '2026-03-15',
  },
  {
    id: 'trip-011',
    title: 'Dzukou Valley Nagaland Trek',
    location: 'Kohima',
    state: 'Nagaland',
    description: 'Trek into the "Valley of Flowers of the Northeast" — the Dzukou Valley, a pristine high-altitude valley on the Nagaland-Manipur border. Famous for the endemic Dzukou lily that blooms every 7 years, this remote trek offers solitude and spectacular scenery.',
    shortDesc: 'Remote high-altitude valley trek on the Nagaland-Manipur border.',
    price: 13500,
    duration: 4,
    difficulty: 'Challenging',
    status: 'Draft',
    startDate: '2026-08-10',
    endDate: '2026-08-13',
    totalSeats: 12,
    bookedSeats: 0,
    images: [
      'https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?w=800&q=80',
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80',
      'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80',
      'https://images.unsplash.com/photo-1542401886-65d6c61db217?w=800&q=80',
    ],
    highlights: ['Endemic Dzukou lily', 'Pristine valley ecosystem', 'Naga village cultural visit', 'Remote wilderness camping'],
    inclusions: ['All accommodation', 'All meals (local Naga cuisine)', 'Guide and permits', 'ILP assistance', 'First aid'],
    exclusions: ['Flight to Dimapur', 'Inner Line Permit fee', 'Personal gear', 'Travel insurance'],
    itinerary: [
      { day: 1, title: 'Kohima to Viswema', description: 'Drive to Viswema village. Trek start briefing and acclimatization.' },
      { day: 2, title: 'Viswema to Dzukou Valley', description: 'Trek to the valley floor (7,874 ft). Set up base camp.' },
      { day: 3, title: 'Valley Exploration', description: 'Full day exploring the valley. Photography and lily spotting.' },
      { day: 4, title: 'Return to Kohima', description: 'Trek back to Viswema. Drive to Kohima. Trip ends.' },
    ],
    meetingPoint: 'Kohima Town Hall, Opposite DC Office',
    cancellationPolicy: 'Full refund up to 20 days before. 50% refund 10–20 days before. No refund within 10 days.',
    minAge: 16,
    maxGroupSize: 12,
    category: 'Trek',
    featured: false,
    createdAt: '2026-03-20',
  },
  {
    id: 'trip-012',
    title: 'Paragliding at Bir Billing',
    location: 'Bir',
    state: 'Himachal Pradesh',
    description: 'Soar above the Kangra Valley from Billing — the world\'s second-highest paragliding site at 8,300 ft. A 25-minute tandem flight over pine forests and Himalayan foothills, landing at the quaint Tibetan colony of Bir. Suitable for all ages.',
    shortDesc: 'Tandem paragliding from Billing — world\'s second highest paragliding site.',
    price: 4500,
    duration: 2,
    difficulty: 'Easy',
    status: 'Published',
    startDate: '2026-05-18',
    endDate: '2026-05-19',
    totalSeats: 24,
    bookedSeats: 16,
    images: [
      'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80',
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
      'https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?w=800&q=80',
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&q=80',
    ],
    highlights: ['World\'s 2nd highest launch site', '25-min tandem flight', 'GoPro video included', 'Tibetan monastery visit'],
    inclusions: ['Tandem paragliding', 'GoPro footage', 'Safety briefing', 'Guesthouse stay', 'Breakfast'],
    exclusions: ['Travel to Bir', 'Lunch and dinner', 'Personal expenses'],
    itinerary: [
      { day: 1, title: 'Arrival and Billing Drive', description: 'Check-in at Bir. Drive up to Billing launch site. Evening monastery visit.' },
      { day: 2, title: 'Paragliding Day', description: 'Morning flight from Billing (8,300 ft) landing at Bir. Certificate and video.' },
    ],
    meetingPoint: 'Bir Tibetan Colony, Main Chowk',
    cancellationPolicy: 'Full refund up to 5 days before. No refund within 5 days.',
    minAge: 14,
    maxGroupSize: 24,
    category: 'Adventure Sports',
    featured: true,
    createdAt: '2026-03-25',
  },
];

export const MOCK_BOOKINGS: Booking[] = [
  {
    id: 'bkg-0001',
    tripId: 'trip-001',
    tripTitle: 'Kedarkantha Winter Trek',
    customerName: 'Arjun Mehta',
    customerPhone: '+91 98765 43210',
    customerEmail: 'arjun.mehta@gmail.com',
    seats: 2,
    travelDate: '2026-05-10',
    totalAmount: 17000,
    paymentStatus: 'Paid',
    bookingStatus: 'Confirmed',
    paymentId: 'pay_QxR7mK9nL2pT',
    createdAt: '2026-04-02',
    notes: 'Requested vegetarian meals only.',
  },
  {
    id: 'bkg-0002',
    tripId: 'trip-002',
    tripTitle: 'Rishikesh River Rafting Expedition',
    customerName: 'Priya Sharma',
    customerPhone: '+91 91234 56789',
    customerEmail: 'priya.s@outlook.com',
    seats: 4,
    travelDate: '2026-05-03',
    totalAmount: 12800,
    paymentStatus: 'Paid',
    bookingStatus: 'Confirmed',
    paymentId: 'pay_NbW4kH8cJ5qA',
    createdAt: '2026-04-05',
    notes: '',
  },
  {
    id: 'bkg-0003',
    tripId: 'trip-001',
    tripTitle: 'Kedarkantha Winter Trek',
    customerName: 'Rahul Verma',
    customerPhone: '+91 87654 32109',
    customerEmail: 'rahulv@yahoo.com',
    seats: 3,
    travelDate: '2026-05-10',
    totalAmount: 25500,
    paymentStatus: 'Partial',
    bookingStatus: 'Pending',
    paymentId: 'pay_CdY2jG6bF1nR',
    createdAt: '2026-04-08',
    notes: 'Paid 50% advance. Balance due by 1 May.',
  },
  {
    id: 'bkg-0004',
    tripId: 'trip-003',
    tripTitle: 'Hampta Pass High Altitude Trek',
    customerName: 'Sneha Patel',
    customerPhone: '+91 99887 76655',
    customerEmail: 'sneha.patel@gmail.com',
    seats: 1,
    travelDate: '2026-06-15',
    totalAmount: 12000,
    paymentStatus: 'Paid',
    bookingStatus: 'Confirmed',
    paymentId: 'pay_LmZ9wX3vU8sB',
    createdAt: '2026-04-10',
    notes: '',
  },
  {
    id: 'bkg-0005',
    tripId: 'trip-004',
    tripTitle: 'Coorg Coffee Plantation Camping',
    customerName: 'Vikram Nair',
    customerPhone: '+91 70123 45678',
    customerEmail: 'vikram.nair@company.co.in',
    seats: 6,
    travelDate: '2026-05-16',
    totalAmount: 33000,
    paymentStatus: 'Unpaid',
    bookingStatus: 'Pending',
    paymentId: '',
    createdAt: '2026-04-12',
    notes: 'Corporate group booking. Invoice requested.',
  },
  {
    id: 'bkg-0006',
    tripId: 'trip-005',
    tripTitle: 'Spiti Valley Motorcycle Expedition',
    customerName: 'Aditya Kumar',
    customerPhone: '+91 95555 11223',
    customerEmail: 'aditya.k@gmail.com',
    seats: 1,
    travelDate: '2026-07-05',
    totalAmount: 28000,
    paymentStatus: 'Paid',
    bookingStatus: 'Confirmed',
    paymentId: 'pay_HkA5cM7dE4fQ',
    createdAt: '2026-04-01',
    notes: 'Needs Royal Enfield Himalayan 450.',
  },
  {
    id: 'bkg-0007',
    tripId: 'trip-006',
    tripTitle: 'Valley of Flowers National Park',
    customerName: 'Meera Krishnan',
    customerPhone: '+91 82345 67890',
    customerEmail: 'meera.k@hotmail.com',
    seats: 2,
    travelDate: '2026-07-20',
    totalAmount: 19600,
    paymentStatus: 'Paid',
    bookingStatus: 'Confirmed',
    paymentId: 'pay_PtB8xN2yW6jV',
    createdAt: '2026-04-14',
    notes: '',
  },
  {
    id: 'bkg-0008',
    tripId: 'trip-002',
    tripTitle: 'Rishikesh River Rafting Expedition',
    customerName: 'Rohan Desai',
    customerPhone: '+91 96321 54870',
    customerEmail: 'rohan.desai@startup.io',
    seats: 5,
    travelDate: '2026-05-03',
    totalAmount: 16000,
    paymentStatus: 'Paid',
    bookingStatus: 'Confirmed',
    paymentId: 'pay_GsC3vL9mK7nD',
    createdAt: '2026-04-03',
    notes: 'Team outing. Need group photo service.',
  },
  {
    id: 'bkg-0009',
    tripId: 'trip-009',
    tripTitle: 'Meghalaya Living Root Bridges Trek',
    customerName: 'Ananya Singh',
    customerPhone: '+91 73456 78901',
    customerEmail: 'ananya.singh@gmail.com',
    seats: 2,
    travelDate: '2026-06-01',
    totalAmount: 23000,
    paymentStatus: 'Partial',
    bookingStatus: 'Pending',
    paymentId: 'pay_EwD6uF4hI1kJ',
    createdAt: '2026-04-15',
    notes: 'First-time trekkers. Extra briefing requested.',
  },
  {
    id: 'bkg-0010',
    tripId: 'trip-012',
    tripTitle: 'Paragliding at Bir Billing',
    customerName: 'Karan Malhotra',
    customerPhone: '+91 88776 65544',
    customerEmail: 'karan.m@yahoo.in',
    seats: 3,
    travelDate: '2026-05-18',
    totalAmount: 13500,
    paymentStatus: 'Unpaid',
    bookingStatus: 'Pending',
    paymentId: '',
    createdAt: '2026-04-17',
    notes: '',
  },
  {
    id: 'bkg-0011',
    tripId: 'trip-007',
    tripTitle: 'Andaman Scuba Diving Package',
    customerName: 'Divya Reddy',
    customerPhone: '+91 94567 89012',
    customerEmail: 'divya.r@gmail.com',
    seats: 2,
    travelDate: '2026-05-25',
    totalAmount: 44000,
    paymentStatus: 'Paid',
    bookingStatus: 'Confirmed',
    paymentId: 'pay_MnO2pQ5rS8tU',
    createdAt: '2026-04-06',
    notes: 'Both guests are PADI Open Water certified.',
  },
  {
    id: 'bkg-0012',
    tripId: 'trip-010',
    tripTitle: 'Chopta Tungnath Chandrashila',
    customerName: 'Suresh Iyer',
    customerPhone: '+91 79012 34567',
    customerEmail: 'suresh.iyer@rediffmail.com',
    seats: 4,
    travelDate: '2026-05-22',
    totalAmount: 26000,
    paymentStatus: 'Refunded',
    bookingStatus: 'Cancelled',
    paymentId: 'pay_VwX1yZ3aB4cD',
    createdAt: '2026-04-09',
    notes: 'Cancelled due to medical emergency. Refund processed.',
  },
];

export const TESTIMONIALS = [
  {
    id: 'test-001',
    name: 'Arjun Mehta',
    location: 'Mumbai',
    trip: 'Kedarkantha Winter Trek',
    rating: 5,
    text: 'The Kedarkantha summit was absolutely magical — pure white snow as far as the eye could see. VinciAdventures organized everything perfectly. The trek leader was knowledgeable and kept the group energized.',
    avatar: 'AM',
  },
  {
    id: 'test-002',
    name: 'Priya Sharma',
    location: 'Bangalore',
    trip: 'Rishikesh River Rafting',
    rating: 5,
    text: 'Best weekend trip I\'ve done! The rapids were thrilling and our guide made sure we were safe throughout. The riverside camp at night was the cherry on top. Already planning my next VinciAdventures trip!',
    avatar: 'PS',
  },
  {
    id: 'test-003',
    name: 'Sneha Patel',
    location: 'Ahmedabad',
    trip: 'Hampta Pass Trek',
    rating: 5,
    text: 'Crossing Hampta Pass and suddenly seeing the Spiti desert from the green Kullu valley was a moment I\'ll never forget. The team was incredibly supportive for my first high-altitude trek.',
    avatar: 'SP',
  },
  {
    id: 'test-004',
    name: 'Vikram Nair',
    location: 'Chennai',
    trip: 'Coorg Camping',
    rating: 4,
    text: 'Perfect corporate outing! The coffee plantation walk was educational and the Kodava dinner was delicious. Our team of 6 had a wonderful time. Great value for money.',
    avatar: 'VN',
  },
];